import { Component } from "@angular/core";
import { forkJoin } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { Department } from "@enums/department.enum";
import { OpsPlanningMode } from "@enums/opsPlanningMode.enum";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { Shift } from "@enums/shift.enum";
import { environment } from "@environments/environment";
import { GetProductionStatusColorOpsPlanner } from "@helpers/getProductionStatusColorOpsPlanner";
import { GetProductionStatusColorOpsPlannerButton } from "@helpers/getProductionStatusColorOpsPlannerButton";
import { GetProductionStatusColorOpsPlannerButtonText } from "@helpers/getProductionStatusColorOpsPlannerButtonText";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ChangeWorkOrderStageDesiredStartTime } from "@interfaces/dto/changeWorkOrderStageDesiredStartTime.interface";
import { ChangeWorkOrderStageEstimatedStartDate } from "@interfaces/dto/changeWorkOrderStageEstimatedStartDate.interface";
import { ChangeWorkOrderStageMachine } from "@interfaces/dto/changeWorkOrderStageMachine.interface";
import { ChangeWorkOrderStageOperator } from "@interfaces/dto/changeWorkOrderStageOperator.interface";
import { ChangeWorkOrderStagePriority } from "@interfaces/dto/changeWorkOrderStagePriority.interface";
import { WorkOrderStageIncrementQuantity } from "@interfaces/workOrderStageIncrementQuantity.interface";
import { Site } from "@interfaces/site.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { MachineService } from "@services/machine.service";
import { SiteService } from "@services/site.service";
import { TrainingService } from "@services/trainingService.service";
import { UserProfileService } from "@services/userProfile.service";
import { WorkOrderService } from "@services/workOrder.service";
import { WorkOrderStageService } from "@services/workOrderStage.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "departments-id",
	styleUrls: ["departments-id.css"],
	templateUrl: "departments-id.html"
})
export class OpsPlanningDepartmentsIdAdminComponent {
	changes: any = [];
	department: Department = Department.Assembly;
	departmentId = 0;
	departments: any;
	dragOverMachineId: number | null = null;
	dragTargetStageId: number | null = null;
	draggedFromMachineId: number | null = null;
	draggedStageId: number | null = null;
	dropdownUserProfiles: UserProfile[] = [];
	editRowKey: any = null;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	isLoading = true;
	machineTrainingRequiredForAssignment = true;
	machines: any[] = [];
	machinesShowingAllAvailable: number[] = [];
	machinesShowingAllPending: number[] = [];
	machinesShowingAllRunning: number[] = [];
	minDate: Date = new Date();
	opsPlanningDate: Date = new Date();
	opsPlanningMode: OpsPlanningMode = OpsPlanningMode.ForDate;
	opsPlanningModes: any;
	popupAddButtonOptions: any;
	popupChangeMachineProductionStatus: { machineId: number, productionStatus: ProductionStatus } = { machineId: 0, productionStatus: ProductionStatus.Active };
	popupChangeWorkOrderStageDesiredStartTime: ChangeWorkOrderStageDesiredStartTime = new ChangeWorkOrderStageDesiredStartTime();
	popupChangeWorkOrderStageEstimatedStartDate: ChangeWorkOrderStageEstimatedStartDate = new ChangeWorkOrderStageEstimatedStartDate();
	popupChangeWorkOrderStageMachine: ChangeWorkOrderStageMachine = new ChangeWorkOrderStageMachine();
	popupChangeWorkOrderStageOperator: ChangeWorkOrderStageOperator = new ChangeWorkOrderStageOperator();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupEstimatedShifts = 0;
	popupIncrementQuantityData: WorkOrderStageIncrementQuantity = new WorkOrderStageIncrementQuantity();
	popupOperatorQueue: any[] = [];
	popupTitle = "Change Machine";
	popupVisible = false;
	priorities: number[];
	productionStatuses: any;
	shifts: any;
	showPausedJobs = true;
	sites: Site[] = [];
	title = "Ops Planning";
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private machineService: MachineService, private siteService: SiteService, private trainingService: TrainingService, private userProfileService: UserProfileService, private workOrderStageService: WorkOrderStageService, private workOrderService: WorkOrderService) {
		this.departments = GetPropertiesFromEnum(Department);
		this.opsPlanningModes = GetPropertiesFromEnum(OpsPlanningMode);
		this.productionStatuses = GetPropertiesFromEnum(ProductionStatus);
		this.shifts = GetPropertiesFromEnum(Shift);
		this.route.params
			.subscribe((params) => {
				this.department = params.department;
				this.departmentId = params.department;
				this.getMachines();
				this.getSites();
				this.getUserProfiles();
			});
		this.getTrainingRequired();
		this.changeWorkOrderStageMachinePopup = this.changeWorkOrderStageMachinePopup.bind(this);
		this.changeOpsPlanningDate = this.changeOpsPlanningDate.bind(this);
		this.changeOpsPlanningMode = this.changeOpsPlanningMode.bind(this);
		this.recalculateStageEstimates = this.recalculateStageEstimates.bind(this);
		this.showRunningSections = this.showRunningSections.bind(this);
		this.updatePopupOperatorQueueOrder = this.updatePopupOperatorQueueOrder.bind(this);
		this.viewToday = this.viewToday.bind(this);

		this.priorities = [];
		for (let i = 1; i <= environment.build.lowestWorkOrderStagePriority; i++) {
			this.priorities.push(i);
		}
	}

	assignNextOperatorInQueue(id: number) {
		if (confirm("Are you sure you wish to assign the next operator in the queue to this stage? This cannot be undone without manually resetting the assignment and queue.") == false) {
			return false;
		}
		this.workOrderStageService.nextOperator(id)
			.subscribe(
				() => {
					this.toastService.success("Work Order Operator assigned from the Queue");
					this.getMachines();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	buildButtons() {
		this.headerTertiaryButtons = [
			{ icon: "fal fa-sync-alt", method: "getMachines", text: "" },
			{ method: "goToFirewall", text: "Firewall" },
			{ method: "goToManForMan", text: "Operator Plan" },
			{ method: "goToWorkforce", text: "Workforce" },
			{ method: "goToOutsideProcessMovements", text: "OPM" }
		];

		this.headerSecondaryButtons = [];

		const filteredDepartments = this.departments.filter((d: { id: number; name: string; }) => (d.id > 0 && d.id < 55) || d.id == 70);
		filteredDepartments.forEach((fd: any) => {
			this.headerSecondaryButtons.push({
				method: "goToDepartment-" + fd.id,
				style: fd.id == this.departmentId ? "background-color: #10B981; color: white;" : "",
				text: fd.name
			});
		});

		this.headerPrimaryButtons = [];

		this.sites.forEach((site: any) => {
			this.headerPrimaryButtons.push({ method: "goToSite-" + site.id, text: site.name });
		});

		const selectedDepartment = this.departments.filter((d: { id: number; name: string; }) => d.id == this.departmentId);
		this.title = selectedDepartment[0].name;
	}

	changeMachineProductionStatus() {
		this.isLoading = true;
		this.machineService.updateProductionStatusSupervisor(this.popupChangeMachineProductionStatus.machineId, this.popupChangeMachineProductionStatus.productionStatus)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Machine Production Status Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeMachineProductionStatusPopup(machineId: number) {
		const selectedMachine = this.machines.filter((m: any) => m.machineId === machineId);
		this.popupChangeMachineProductionStatus.machineId = machineId;
		this.popupChangeMachineProductionStatus.productionStatus = selectedMachine[0].productionStatus;
		this.popupAddButtonOptions = { onClick: () => this.changeMachineProductionStatus(), text: "Change Production Status" };
		this.popupTitle = "Change Production Status";
		this.popupVisible = true;
		return false;
	}

	changeOpsPlanningDate(e: any) {
		this.opsPlanningDate = e.value;
		this.getMachines();
	}

	changeOpsPlanningMode(e: any) {
		this.opsPlanningMode = e.value;
		this.getMachines();
	}

	changeWorkOrderStageDesiredStartTime() {
		this.isLoading = true;
		this.workOrderStageService.changeDesiredStartTime(this.popupChangeWorkOrderStageDesiredStartTime)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order Stage Start Time Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageDesiredStartTimePopup(stageId: number) {
		this.popupChangeWorkOrderStageDesiredStartTime = new ChangeWorkOrderStageDesiredStartTime();
		this.popupChangeWorkOrderStageDesiredStartTime.workOrderStageId = stageId;
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageDesiredStartTime(), text: "Change Time" };
		this.popupTitle = "Change Desired Start Time";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderStageEstimatedStartDate() {
		this.isLoading = true;
		this.workOrderStageService.changeEstimatedStartDate(this.popupChangeWorkOrderStageEstimatedStartDate)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order Stage Estimated Start Date Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageEstimatedStartDatePopup(stageId: number) {
		this.popupChangeWorkOrderStageEstimatedStartDate = new ChangeWorkOrderStageEstimatedStartDate();
		this.popupChangeWorkOrderStageEstimatedStartDate.workOrderStageId = stageId;
		this.popupChangeWorkOrderStageEstimatedStartDate.estimatedStartDate = this.opsPlanningDate;
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageEstimatedStartDate(), text: "Change Date" };
		this.popupTitle = "Change Estimated Start Date";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderStageMachine() {
		this.isLoading = true;
		this.workOrderStageService.changeMachine(this.popupChangeWorkOrderStageMachine)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order Stage Machine Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageMachinePopup(stageId: number) {
		this.popupChangeWorkOrderStageMachine = new ChangeWorkOrderStageMachine();
		this.popupChangeWorkOrderStageMachine.workOrderStageId = stageId;
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageMachine(), text: "Change Machine" };
		this.popupTitle = "Change Machine";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderStageOperator() {
		this.isLoading = true;
		this.popupChangeWorkOrderStageOperator.operatorQueue = JSON.stringify(this.popupOperatorQueue);
		this.workOrderStageService.changeOperator(this.popupChangeWorkOrderStageOperator)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order Stage Operators Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageOperatorPopup(stageId: number, machineId: number, isRunningStage: boolean) {
		if (machineId === undefined || machineId <= 0) {
			alert("Please assign this stage to a machine first.");
			return false;
		}

		const selectedMachine = this.machines.filter((m: any) => m.machineId === machineId)[0];
		const selectedWorkOrderStage = isRunningStage ? selectedMachine.runningStages.filter((s: any) => s.id === stageId)[0] : selectedMachine.availableStages.filter((s: any) => s.id === stageId)[0];

		// Pull details from the machines
		this.popupChangeWorkOrderStageOperator = new ChangeWorkOrderStageOperator();
		this.popupChangeWorkOrderStageOperator.workOrderStageId = stageId;
		this.popupChangeWorkOrderStageOperator.userProfileId = selectedWorkOrderStage.assignedUserProfileId;
		this.popupChangeWorkOrderStageOperator.operatorQueue = selectedWorkOrderStage.operatorQueue;
		this.popupEstimatedShifts = selectedWorkOrderStage.estimatedShifts;
		this.popupOperatorQueue = JSON.parse(selectedWorkOrderStage.operatorQueue);
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageOperator(), text: "Change Operators" };
		this.isLoading = true;
		if (this.machineTrainingRequiredForAssignment === false) {
			this.userProfileService.byMachine(machineId)
				.subscribe(
					(res: any) => {
						this.dropdownUserProfiles = res.response;
						this.dropdownUserProfiles.sort(function (a, b) {
							return a.shiftId as number - b.shiftId as number || a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
						});
						this.popupTitle = "Change Operators";
						this.popupVisible = true;
						this.isLoading = false;
					},
					(err: any) => {
						this.isLoading = false;
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					}
				);
		} else {
			this.trainingService.usersTrainedForMachine(machineId)
				.subscribe(
					(res: any) => {
						this.dropdownUserProfiles = res.response;
						this.dropdownUserProfiles.sort(function (a, b) {
							return a.shiftId as number - b.shiftId as number || a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
						});
						this.popupTitle = "Change Operators";
						this.popupVisible = true;
						this.isLoading = false;
					},
					(err: any) => {
						this.isLoading = false;
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					}
				);
		}

		return false;
	}

	changeWorkOrderStagePriority(stageId: number, priority: number) {
		this.isLoading = true;
		const data = new ChangeWorkOrderStagePriority();
		data.priority = priority;
		data.workOrderStageId = stageId;
		this.workOrderStageService.changePriority(data)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order Stage Priority Changed");
					this.getMachines();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	changeWorkOrderStagePriorityDropDown(args: any, stageId: number) {
		const newPriority = args.value;

		this.changeWorkOrderStagePriority(stageId, newPriority);
	}

	closePopup() {
		this.popupVisible = false;
	}

	displayMachineInDropdown(e: any) { return e === null ? null : e.cellNumber === null ? e.machineName : `${e.cellNumber} - ${e.machineName}`; }

	displayUserProfileInDropdown(e: any) {
		return e === null ? null : `${e.firstName} ${e.lastName} - ${(e.shiftId === Shift.Day ? "Day" : "Night")} Shift`;
	}

	filteredAvailableStages(machine: any): any[] {
		return this.showPausedJobs ? machine.availableStages : machine.availableStages.filter((s: any) => !s.isPaused);
	}

	filteredRunningStages(machine: any): any[] {
		return this.showPausedJobs ? machine.runningStages : machine.runningStages.filter((s: any) => !s.isPaused);
	}

	generateAndDownloadWorkOrderMasterLabelPDF(workOrderId: number) {
		this.workOrderService.generateAndDownloadMasterLabelPdf(workOrderId).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", "Work_Order_Master_Label_" + workOrderId + ".pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			},
		);
	}

	getMachines() {
		// this.machines = [];
		this.isLoading = true;
		switch (this.opsPlanningMode) {
			case OpsPlanningMode.All:
				this.machineService.opsPlannerForDepartment(this.department)
					.subscribe(
						(res: any) => {
							this.machines = res.response;
							this.isLoading = false;
						},
						(err) => {
							this.isLoading = false;
							console.log(err);
							this.toastService.error("Something Went Wrong!");
						}
					);
				this.isLoading = false;
				break;
			case OpsPlanningMode.ForDate:
				this.machineService.opsPlannerForDepartmentAndDate(this.department, this.opsPlanningDate)
					.subscribe(
						(res: any) => {
							this.machines = res.response;
							this.isLoading = false;
						},
						(err) => {
							this.isLoading = false;
							console.log(err);
							this.toastService.error("Something Went Wrong!");
						}
					);
				break;
			case OpsPlanningMode.Unestimated:
				this.machineService.opsPlannerUnestimatedForDepartment(this.department)
					.subscribe(
						(res: any) => {
							this.machines = res.response;
							this.isLoading = false;
						},
						(err) => {
							this.isLoading = false;
							console.log(err);
							this.toastService.error("Something Went Wrong!");
						}
					);
				break;
		}

	}

	getSites() {
		this.isLoading = true;
		this.siteService.getAll()
			.subscribe(
				(res: any) => {
					this.sites = res.response.items;
					this.buildButtons();
					this.isLoading = false;
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getTrainingRequired() {
		this.workOrderStageService.machineTrainingRequiredForAssignment()
			.subscribe(
				(res: any) => {
					this.machineTrainingRequiredForAssignment = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getUserProfiles() {
		this.isLoading = true;
		this.userProfileService.getActiveUsers()
			.subscribe(
				(res: any) => {
					this.userProfiles = res.response;
					this.userProfiles.sort(function (a, b) {
						return a.shiftId as number - b.shiftId as number || a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
					});

					this.dropdownUserProfiles = this.userProfiles;
					this.isLoading = false;
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getVisibleQueue(machine: any) {
		return machine.expanded ? machine.queue : machine.queue.slice(0, 2);
	}

	goToDepartment(department: number) {
		this.router.navigate([`/admin/opsPlanning/department/${department}`]);
	}

	goToFirewall() {
		this.router.navigate(["/admin/opsPlanning/firewall"]);
	}

	goToManForMan() {
		this.router.navigate(["/admin/opsPlanning/operatorPlan"]);
	}

	goToOutsideProcessMovements() {
		this.router.navigate(["/admin/opsPlanning/outsideProcessContractMovements"]);
	}

	goToSite(site: number) {
		this.router.navigate([`/admin/opsPlanning/site/${site}`]);
	}

	goToWorkforce() {
		this.router.navigate(["/admin/opsPlanning/workforce"]);
	}

	headerButtonClick(method: string) {
		if (method.indexOf("-") > 0) {
			const split = method.split("-");
			// @ts-ignore // Required to be able to call the method directly from the variable
			this[split[0]](split[1]);
		}
		else {
			// @ts-ignore // Required to be able to call the method directly from the variable
			if (this[method]) this[method]();
		}
	}

	incrementWorkOrderStage() {
		if (this.popupIncrementQuantityData.quantity > this.popupIncrementQuantityData.maximumQuantity) {
			alert("Quantity too high");
			return;
		}
		this.workOrderStageService.completedQuantity(this.popupIncrementQuantityData.id, this.popupIncrementQuantityData.quantity)
			.subscribe(
				() => {
					this.toastService.success("Completed Quantity Incremented");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	incrementWorkOrderStagePopup(stageId: number, quantityCompleted: number, quantityRequired: number) {
		this.popupIncrementQuantityData = new WorkOrderStageIncrementQuantity();
		this.popupIncrementQuantityData.id = stageId;
		this.popupIncrementQuantityData.maximumQuantity = quantityRequired - quantityCompleted;
		this.popupIncrementQuantityData.quantity = 1;
		this.popupAddButtonOptions = { onClick: () => this.incrementWorkOrderStage(), text: "Increment Completed Quantity" };
		this.popupTitle = "Increment Completed Quantity";
		this.popupVisible = true;
	}

	// onDrop(machine: any, event: CdkDragDrop<any[]>) {
	// 	moveItemInArray(machine.queue, event.previousIndex, event.currentIndex);
	// 	// Add your trigger here, e.g. call a service or emit event
	// 	console.log("Queue reordered:", machine.queue);
	// }

	onDragEnd() {
		this.draggedStageId = null;
		this.draggedFromMachineId = null;
		this.dragOverMachineId = null;
		this.dragTargetStageId = null;
	}

	onDragLeave(machineId: number) {
		if (this.dragOverMachineId === machineId) {
			this.dragOverMachineId = null;
		}
	}

	onDragLeaveStage(stageId: number) {
		if (this.dragTargetStageId === stageId) {
			this.dragTargetStageId = null;
		}
	}

	onDragOver(event: DragEvent, machineId: number) {
		event.preventDefault();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}
		this.dragOverMachineId = machineId;
	}

	onDragOverStage(event: DragEvent, stageId: number, machineId: number) {
		event.preventDefault();
		event.stopPropagation();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}
		this.dragOverMachineId = machineId;
		if (this.draggedFromMachineId === machineId && stageId !== this.draggedStageId) {
			this.dragTargetStageId = stageId;
		}
	}

	onDragStart(event: DragEvent, stageId: number, machineId: number) {
		this.draggedStageId = stageId;
		this.draggedFromMachineId = machineId;
		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = "move";
		}
	}

	onDropOnMachine(targetMachineId: number) {
		if (this.draggedStageId === null || this.draggedFromMachineId === targetMachineId) {
			this.onDragEnd();
			return;
		}
		const data = new ChangeWorkOrderStageMachine();
		data.workOrderStageId = this.draggedStageId;
		data.machineId = targetMachineId;
		this.isLoading = true;
		this.workOrderStageService.changeMachine(data)
			.subscribe(
				() => {
					this.isLoading = false;
					this.toastService.success("Work Order moved to new machine");
					this.getMachines();
					this.onDragEnd();
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					this.onDragEnd();
				}
			);
	}

	onDropOnStage(event: DragEvent, targetStageId: number, targetMachineId: number) {
		event.stopPropagation();
		if (this.draggedStageId === null || this.draggedStageId === targetStageId) {
			this.onDragEnd();
			return;
		}
		if (this.draggedFromMachineId === targetMachineId) {
			this.reorderWithinMachine(targetMachineId, this.draggedStageId, targetStageId);
		} else {
			this.onDropOnMachine(targetMachineId);
		}
	}

	onStatusChange(machine: any, newStatus: string) {
		machine.status = newStatus;
		// Optionally: emit or call a service
	}

	recalculateStageEstimates(stageId: number) {
		this.isLoading = true;
		this.workOrderStageService.recalculateEstimates(stageId)
			.subscribe(
				() => {
					this.isLoading = false;
					this.getMachines();
					this.toastService.success("Estimates Reworked, reloading details.");
				},
				(err) => {
					this.isLoading = false;
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
		return false;
	}

	renderDateString(date: Date) {
		const dateToChange = new Date(date);
		return dateToChange.toLocaleDateString("en-GB");
	}

	renderStatusBackgroundColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlanner(status);
	}

	renderStatusButtonColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlannerButton(status);
	}

	renderStatusButtonTextColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlannerButtonText(status);
	}


	renderStatusColor(status: ProductionStatus) {
		return GetProductionStatusColorOpsPlannerButtonText(status);
	}

	reorderWithinMachine(machineId: number, draggedId: number, targetId: number) {
		const machine = this.machines.find((m: any) => m.machineId === machineId);
		if (!machine) { this.onDragEnd(); return; }

		const stages = [...machine.availableStages].sort((a: any, b: any) => a.priority - b.priority);
		const fromIndex = stages.findIndex((s: any) => s.id === draggedId);
		const toIndex = stages.findIndex((s: any) => s.id === targetId);

		if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
			this.onDragEnd();
			return;
		}

		const [moved] = stages.splice(fromIndex, 1);
		stages.splice(toIndex, 0, moved);

		const updates = stages
			.map(( index: number, stage: any) => ({ newPriority: index + 1, stage }))
			.filter(({ newPriority, stage }: { newPriority: number, stage: any }) => stage.priority !== newPriority)
			.map(({ newPriority, stage }: { newPriority: number, stage: any }) => {
				const data = new ChangeWorkOrderStagePriority();
				data.workOrderStageId = stage.id;
				data.priority = newPriority;
				return this.workOrderStageService.changePriority(data);
			});

		if (updates.length === 0) {
			this.onDragEnd();
			return;
		}

		this.isLoading = true;
		forkJoin(updates).subscribe(
			() => {
				this.isLoading = false;
				this.toastService.success("Queue order updated");
				this.getMachines();
				this.onDragEnd();
			},
			(err: any) => {
				this.isLoading = false;
				console.log(err);
				this.toastService.error("Something Went Wrong!");
				this.onDragEnd();
			}
		);
	}

	setAssignedOperatorsForMachine(machineId: number) {
		if (this.opsPlanningMode !== OpsPlanningMode.ForDate) {
			return false;
		}
		const machine = this.machines.filter(m => m.machineId == machineId)[0];
		if (machine.runningStages.length < 1) {
			alert(`${machine.cellNumber} has no Running Stages`);
			return false;
		}
		if (machine.availableStages.length < 1) {
			alert(`${machine.cellNumber} has no Pending Stages`);
			return false;
		}

		if (confirm(`Please confirm you wisth to change the operators for queued stages on ${machine.cellNumber} to match the first running stage.`) == false) {
			return false;
		}

		const mainStage = machine.runningStages[0];
		const assignedUserProfileId = mainStage.assignedUserProfileId;

		const availableStagesToChange = machine.availableStages.filter((a: { assignedUserProfileId: number, id: number, operatorQueue: string; }) => a.assignedUserProfileId != assignedUserProfileId);

		if (availableStagesToChange.length == 0) {
			return false;
		}
		
		this.isLoading = true;

		for (let i = 0; i < availableStagesToChange.length; i++) {
			const availableStage = availableStagesToChange[i];
			
			const changeOperatorDto = new ChangeWorkOrderStageOperator();
			changeOperatorDto.workOrderStageId = availableStage.id;
			changeOperatorDto.userProfileId = assignedUserProfileId;
			changeOperatorDto.operatorQueue = availableStage.operatorQueue;

			// submit to the change operator endpoint here
			this.workOrderStageService.changeOperator(changeOperatorDto)
				.subscribe(
					() => {
						this.toastService.success("Work Order Stage Operators Updated");
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}

		this.isLoading = false;
		this.getMachines();

		return false;
	}

	showRunningSections() {
		return this.opsPlanningMode === OpsPlanningMode.ForDate && this.opsPlanningDate.toDateString() === this.minDate.toDateString();
	}

	toggleExpand(machine: any) {
		machine.expanded = !machine.expanded;
	}

	toggleMachineShowingAllAvailable(machineId: number) {
		console.log(machineId);
		const currentIndex = this.machinesShowingAllAvailable.indexOf(machineId);
		if (currentIndex >= 0) {
			this.machinesShowingAllAvailable.splice(currentIndex);
			console.log("REMOVING");
		} else {
			this.machinesShowingAllAvailable.push(machineId);
			console.log("ADDING");
		}
		return false;
	}

	toggleMachineShowingAllPending(machineId: number) {
		const currentIndex = this.machinesShowingAllPending.indexOf(machineId);
		if (currentIndex >= 0) {
			this.machinesShowingAllPending.splice(currentIndex);
		} else {
			this.machinesShowingAllPending.push(machineId);
		}
		return false;
	}

	toggleMachineShowingAllRunning(machineId: number) {
		const currentIndex = this.machinesShowingAllRunning.indexOf(machineId);
		if (currentIndex >= 0) {
			this.machinesShowingAllRunning.splice(currentIndex);
		} else {
			this.machinesShowingAllRunning.push(machineId);
		}
		return false;
	}

	toggleStagePause(workOrderStageId: number) {
		this.workOrderStageService.togglePause(workOrderStageId)
			.subscribe(
				() => {
					this.toastService.success("Successfully Toggled Pause");
					this.getMachines();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
		return false;
	}

	updatePopupOperatorQueueOrder(e: any) {
		const visibleRows = e.component.getVisibleRows();
		const toIndex = this.popupOperatorQueue.indexOf(visibleRows[e.toIndex].data);
		const fromIndex = this.popupOperatorQueue.indexOf(e.itemData);
	
		this.popupOperatorQueue.splice(fromIndex, 1);
		this.popupOperatorQueue.splice(toIndex, 0, e.itemData);
	}

	viewToday() {
		this.opsPlanningDate = new Date();
		this.opsPlanningMode = OpsPlanningMode.ForDate;
	}
}